import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { mapApiErrorToUiModel, resolveUiErrorCopy } from '@formosa/shared/i18n/mapApiErrorToUiModel';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { journeyApi } from '../../api/journeyApi';
import { journeyDecisionApi } from '../../api/journeyDecisionApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { useAuth } from '../../auth/AuthContext';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui';
import {
  DECISIONS_PRODUCT_CHROME,
  decisionStatusProductLabel,
  fillWorkspaceProductChrome,
  WORKSPACE_PRODUCT_CHROME,
} from '../journey-workspace/journeyWorkspacePresentation';
import {
  classifyJourneyAccessError,
  normalizeJourneyWorkspace,
} from '../journey-workspace/journeyWorkspaceModel';
import JourneyWorkspaceNav from '../journey-workspace/JourneyWorkspaceNav';
import {
  canCreateDecision,
  classifyDecisionError,
  normalizeDecisionList,
  partitionDecisionList,
} from './journeyDecisionModel';
import '../journey-workspace/journey-workspace.css';
import '../journey-workspace/journey-logistics.css';
import './journey-decision.css';

function statusClass(status) {
  if (status === 'OPEN') return 'is-open';
  if (status === 'DRAFT') return 'is-draft';
  if (status === 'CLOSED') return 'is-closed';
  if (status === 'CANCELLED') return 'is-cancelled';
  return '';
}

function DecisionCard({ item, journeyId }) {
  const statusLabel = decisionStatusProductLabel(item.decisionStatus);
  return (
    <li>
      <Link
        className="journey-decision__card"
        to={`/journeys/${journeyId}/decisions/${item.decisionId}`}
      >
        <p className="journey-decision__card-title">{item.title}</p>
        <p className="journey-decision__meta">
          <span className={`journey-decision__status ${statusClass(item.decisionStatus)}`}>
            {statusLabel}
          </span>
          <span>{item.decisionTypeLabel}</span>
          {item.createTime ? (
            <time dateTime={item.createTime}>{formatDateTime(item.createTime)}</time>
          ) : null}
          {item.deadlineAt ? (
            <span>{DECISIONS_PRODUCT_CHROME.deadlinePrefix} {formatDateTime(item.deadlineAt)}</span>
          ) : null}
          <span>
            {fillWorkspaceProductChrome(DECISIONS_PRODUCT_CHROME.optionCount, {
              count: item.optionCount,
            })}
          </span>
          {item.decisionStatus === 'OPEN' || item.decisionStatus === 'CLOSED' ? (
            <span>
              {fillWorkspaceProductChrome(DECISIONS_PRODUCT_CHROME.participatedCount, {
                count: item.participationCount,
              })}
            </span>
          ) : null}
          {item.actorHasVoted ? <span>{DECISIONS_PRODUCT_CHROME.youVoted}</span> : null}
          {item.selectedOptionId ? <span>{DECISIONS_PRODUCT_CHROME.resolved}</span> : null}
        </p>
      </Link>
    </li>
  );
}

export default function JourneyDecisionsPage() {
  const { journeyId } = useParams();
  const { t } = useTravelTranslation();
  const { user } = useAuth();
  const viewerMemberId = user?.memberId ?? user?.id;
  const generation = useRef(0);

  const [phase, setPhase] = useState('loading');
  const [errorModel, setErrorModel] = useState(null);
  const [journey, setJourney] = useState(null);
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    const requestId = ++generation.current;
    setPhase('loading');
    setErrorModel(null);
    try {
      const [detailPayload, listPayload] = await Promise.all([
        journeyApi.getDetail(journeyId),
        journeyDecisionApi.list(journeyId),
      ]);
      if (requestId !== generation.current) return;
      setJourney(normalizeJourneyWorkspace(detailPayload, viewerMemberId));
      setItems(normalizeDecisionList(listPayload));
      setPhase('ready');
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = classifyDecisionError(cause);
      const access = classifyJourneyAccessError(cause);
      if (kind === 'journey-not-found' || kind === 'not-found' || access === 'not-found') {
        setPhase('not-found');
      } else if (kind === 'forbidden' || access === 'forbidden') {
        setPhase('forbidden');
      } else {
        setErrorModel(mapApiErrorToUiModel(cause, { t }));
        setPhase('error');
      }
    }
  }, [journeyId, t, viewerMemberId]);

  useEffect(() => {
    if (!/^[1-9]\d*$/.test(journeyId || '')) {
      setPhase('not-found');
      return undefined;
    }
    load();
    return () => { generation.current += 1; };
  }, [journeyId, load]);

  if (phase === 'loading') {
    return (
      <main className="journey-workspace-state">
        <LoadingState label={t('travel.common.loading')} data-testid="decisions-loading" />
      </main>
    );
  }
  if (phase === 'not-found') {
    return (
      <main className="journey-workspace-state" data-testid="decisions-not-found">
        <h1>{WORKSPACE_PRODUCT_CHROME.notFoundTitle}</h1>
        <p>{WORKSPACE_PRODUCT_CHROME.notFoundBody}</p>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>
      </main>
    );
  }
  if (phase === 'forbidden') {
    return (
      <main className="journey-workspace-state" data-testid="decisions-forbidden">
        <h1>{WORKSPACE_PRODUCT_CHROME.forbiddenTitle}</h1>
        <p>{WORKSPACE_PRODUCT_CHROME.forbiddenBody}</p>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>
      </main>
    );
  }
  if (phase === 'error') {
    const errorCopy = errorModel ? resolveUiErrorCopy(errorModel, t) : null;
    return (
      <main className="journey-workspace-state" data-testid="decisions-error">
        <ErrorState
          title={errorCopy?.title || t('travel.common.apiError.unknown.title')}
          description={errorCopy?.description || t('travel.common.apiError.unknown.description')}
          retryable={Boolean(errorCopy?.retryable)}
          retryLabel={t('travel.common.retry')}
          onRetry={load}
        />
      </main>
    );
  }

  const allowCreate = canCreateDecision({
    role: journey?.requesterRole,
    lifecycleStatus: journey?.lifecycleStatus,
  });
  const { active, history } = partitionDecisionList(items);

  return (
    <main className="journey-workspace">
      <header className="journey-workspace__header">
        <div>
          <p className="journey-workspace__eyebrow">{WORKSPACE_PRODUCT_CHROME.eyebrow}</p>
          <h1>{journey?.title || DECISIONS_PRODUCT_CHROME.untitledJourney}</h1>
          <p className="journey-workspace__meta">
            {journey?.lifecycleStatusLabel}
            {journey?.requesterRoleLabel ? ` · ${journey.requesterRoleLabel}` : ''}
          </p>
        </div>
        <Link className="journey-workspace__back" to="/journeys">{WORKSPACE_PRODUCT_CHROME.myJourneys}</Link>
      </header>
      <JourneyWorkspaceNav journeyId={journey.journeyId} />

      <section className="journey-workspace__panel journey-decision" aria-labelledby="decisions-heading">
        <div className="journey-workspace__panel-head">
          <h2 id="decisions-heading">{DECISIONS_PRODUCT_CHROME.title}</h2>
        </div>

        <div className="journey-decision__toolbar">
          {allowCreate ? (
            <Link to={`/journeys/${journey.journeyId}/decisions/new`}>
              {DECISIONS_PRODUCT_CHROME.createDecision}
            </Link>
          ) : (
            <p className="journey-decision__lifecycle-note">
              {DECISIONS_PRODUCT_CHROME.createLifecycleGate}
            </p>
          )}
        </div>

        {items.length === 0 ? (
          <EmptyState
            className="journey-decision__empty"
            data-testid="decisions-empty"
            title={DECISIONS_PRODUCT_CHROME.emptyTitle}
            description={DECISIONS_PRODUCT_CHROME.emptyDescription}
            action={allowCreate ? (
              <Link to={`/journeys/${journey.journeyId}/decisions/new`}>
                {DECISIONS_PRODUCT_CHROME.createFirst}
              </Link>
            ) : null}
          />
        ) : (
          <>
            <section className="journey-decision__section" aria-labelledby="active-decisions-heading">
              <h3 id="active-decisions-heading">{DECISIONS_PRODUCT_CHROME.sectionActive}</h3>
              {active.length === 0 ? (
                <p className="journey-decision__hint" role="status">
                  {DECISIONS_PRODUCT_CHROME.activeEmpty}
                </p>
              ) : (
                <ul className="journey-decision__list">
                  {active.map((item) => (
                    <DecisionCard key={item.decisionId} item={item} journeyId={journey.journeyId} />
                  ))}
                </ul>
              )}
            </section>

            <section className="journey-decision__section" aria-labelledby="history-decisions-heading">
              <h3 id="history-decisions-heading">{DECISIONS_PRODUCT_CHROME.sectionHistory}</h3>
              {history.length === 0 ? (
                <p className="journey-decision__hint" role="status">
                  {DECISIONS_PRODUCT_CHROME.historyEmpty}
                </p>
              ) : (
                <ul className="journey-decision__list">
                  {history.map((item) => (
                    <DecisionCard key={item.decisionId} item={item} journeyId={journey.journeyId} />
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </section>
    </main>
  );
}
